import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { normalizeName } from '@obraplan/core/domain/shared/naming';
import {
  MAX_CHECKLIST_ITEMS,
  type ServiceChecklist,
} from '@obraplan/core/domain/quality/service-checklist';
import type { Discipline } from '@obraplan/core/domain/structure/discipline';
import type { Service } from '@obraplan/core/domain/takeoff/service';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';

/** A meta de amostragem com que a ficha nasce — a do cliente (RN-299). */
export const DEFAULT_SAMPLING_PCT = 40;

export interface ChecklistItemSubmit {
  readonly id?: string;
  readonly label: string;
  readonly method?: string;
  readonly tolerance?: string;
}

export interface ChecklistFormSubmit {
  readonly name: string;
  readonly code?: string;
  readonly samplingPct?: number;
  readonly serviceIds: readonly string[];
  readonly items: readonly ChecklistItemSubmit[];
}

interface LinhaDeItem {
  readonly id?: string;
  readonly label: string;
  readonly method: string;
  readonly tolerance: string;
}

/**
 * Cadastrar ou corrigir uma checklist de qualidade — a FVS (Fase 19, fatia 5).
 *
 * O mesmo formulário serve aos dois casos, como o da disciplina. O que muda de verdade em
 * relação a ela é a **lista de serviços**: desde a Fase 21 a ficha aponta para o serviço do
 * cadastro global, por id (ADR-138), e a escolha se faz marcando na lista dele. Renomear o
 * serviço deixou de desfazer o vínculo, que era o defeito que a ligação por nome tinha.
 *
 * São mais de cinquenta serviços, e serão mais: a lista tem busca (ADR-139). O serviço já
 * marcado continua marcado mesmo quando a busca o esconde — filtrar é ver menos, não
 * desmarcar.
 */
export function ChecklistFormDialog({
  open,
  onOpenChange,
  checklist,
  services,
  disciplines,
  suggested,
  onSubmit,
  isPending,
  errorMessage,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Ausente = checklist nova. */
  checklist: ServiceChecklist | undefined;
  /** O cadastro global, na ordem de execução da obra — é dele que a ficha escolhe. */
  services: readonly Service[];
  disciplines: readonly Discipline[];
  /**
   * O ponto de partida de uma ficha nova, quando alguém chega aqui por um caminho que já
   * sabe do que ela trata — cadastrar um serviço oferece criar a ficha dele (RN-235).
   *
   * Só vale para ficha nova: editar parte do que está gravado, sempre.
   */
  suggested?: { readonly name: string; readonly serviceIds: readonly string[] };
  onSubmit: (input: ChecklistFormSubmit) => void;
  isPending: boolean;
  errorMessage: string | undefined;
}) {
  const isEditing = checklist !== undefined;
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [sampling, setSampling] = useState('');
  const [marcados, setMarcados] = useState<readonly string[]>([]);
  const [buscaServico, setBuscaServico] = useState('');
  const [itens, setItens] = useState<readonly LinhaDeItem[]>([]);

  useEffect(() => {
    if (!open) return;
    setName(checklist?.name ?? suggested?.name ?? '');
    setCode(checklist?.code ?? '');
    // A ficha nova nasce com a meta do cliente (RN-299); a que já existe mostra a dela, e a
    // que não tem (criada à mão, antes) continua sem: editar não inventa meta.
    setSampling(
      checklist === undefined
        ? String(DEFAULT_SAMPLING_PCT)
        : checklist.samplingPct === undefined
          ? ''
          : String(checklist.samplingPct),
    );
    setMarcados(checklist?.serviceIds ?? suggested?.serviceIds ?? []);
    setBuscaServico('');
    setItens(
      checklist === undefined
        ? [{ label: '', method: '', tolerance: '' }]
        : checklist.items.map((item) => ({
            id: item.id,
            label: item.label,
            method: item.method ?? '',
            tolerance: item.tolerance ?? '',
          })),
    );
  }, [open, checklist, suggested]);

  const marcada = new Set(marcados);
  const alternarServico = (serviceId: string, marcar: boolean) => {
    setMarcados((atuais) =>
      marcar
        ? atuais.includes(serviceId)
          ? atuais
          : [...atuais, serviceId]
        : atuais.filter((id) => id !== serviceId),
    );
  };

  // A lista, agrupada pela disciplina dona e filtrada pela busca. O serviço marcado nunca
  // é filtrado para fora: quem busca quer achar mais um, não perder de vista o que já tem.
  const termo = normalizeName(buscaServico);
  const nomeDaDisciplina = new Map(disciplines.map((item) => [item.id as string, item.name]));
  const visiveis = services.filter(
    (service) =>
      marcada.has(service.id) ||
      termo === '' ||
      normalizeName(service.name).includes(termo) ||
      normalizeName(nomeDaDisciplina.get(service.disciplineId) ?? '').includes(termo),
  );
  const porDisciplina = new Map<string, Service[]>();
  for (const service of visiveis) {
    const chave = nomeDaDisciplina.get(service.disciplineId) ?? 'Sem disciplina';
    porDisciplina.set(chave, [...(porDisciplina.get(chave) ?? []), service]);
  }

  const alterarItem = (indice: number, mudanca: Partial<LinhaDeItem>) => {
    setItens((atuais) =>
      atuais.map((item, posicao) => (posicao === indice ? { ...item, ...mudanca } : item)),
    );
  };

  const temItem = itens.some((item) => item.label.trim() !== '');
  const amostragem = sampling.trim() === '' ? undefined : Number(sampling);
  const amostragemValida =
    amostragem === undefined ||
    (Number.isInteger(amostragem) && amostragem >= 1 && amostragem <= 100);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar checklist' : 'Nova checklist'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'A mudança vale para a próxima medição. O que já foi respondido continua como foi respondido.'
              : 'A ficha de verificação de um serviço: o que se olha, como se olha e o quanto pode desviar.'}
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto pr-1"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit({
              name,
              serviceIds: marcados,
              items: itens.map((item) => ({
                label: item.label,
                ...(item.id === undefined ? {} : { id: item.id }),
                ...(item.method.trim() === '' ? {} : { method: item.method }),
                ...(item.tolerance.trim() === '' ? {} : { tolerance: item.tolerance }),
              })),
              ...(code.trim() === '' ? {} : { code: code.trim() }),
              ...(amostragem === undefined ? {} : { samplingPct: amostragem }),
            });
          }}
        >
          <div className="grid gap-4 md:grid-cols-[1fr_10rem_8rem]">
            <Field
              label="Nome da checklist"
              htmlFor="checklist-form-name"
              required
              error={errorMessage}
            >
              <Input
                id="checklist-form-name"
                autoFocus
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Execução de Alvenaria Não Estrutural"
              />
            </Field>
            <Field label="Código" htmlFor="checklist-form-code" hint="FORM.109">
              <Input
                id="checklist-form-code"
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
            </Field>
            <Field
              label="Amostragem (%)"
              htmlFor="checklist-form-sampling"
              hint="A meta de pavimentos inspecionados: a tela Qualidade por serviço alerta quando a obra está abaixo dela."
              {...(amostragemValida ? {} : { error: 'De 1 a 100.' })}
            >
              <Input
                id="checklist-form-sampling"
                type="number"
                inputMode="numeric"
                min={1}
                max={100}
                value={sampling}
                onChange={(event) => setSampling(event.target.value)}
              />
            </Field>
          </div>

          <fieldset className="flex flex-col gap-2">
            <legend className="text-sm font-medium">Aplica-se aos serviços</legend>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-(--color-content-muted)">
                O cadastro de Configurações → Serviços. A ficha vale em toda obra que executar o
                serviço marcado.
              </p>
              <Input
                aria-label="Buscar serviço na ficha"
                className="h-9 w-full md:w-56"
                placeholder="Buscar serviço, disciplina…"
                value={buscaServico}
                onChange={(event) => setBuscaServico(event.target.value)}
              />
            </div>
            <div className="grid max-h-64 gap-3 overflow-y-auto rounded-md border border-(--color-border-subtle) p-3 md:grid-cols-2">
              {porDisciplina.size === 0 ? (
                <p className="text-sm text-(--color-content-muted)">
                  {services.length === 0
                    ? 'Nenhum serviço cadastrado ainda. Cadastre em Configurações → Serviços.'
                    : 'Nenhum serviço corresponde à busca.'}
                </p>
              ) : (
                [...porDisciplina].map(([disciplina, doGrupo]) => (
                  <fieldset key={disciplina} className="flex flex-col gap-1">
                    <legend className="text-xs font-medium uppercase tracking-wide text-(--color-content-muted)">
                      {disciplina}
                    </legend>
                    {doGrupo.map((service) => (
                      <label key={service.id} className="flex min-h-9 items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          aria-label={`${disciplina} > ${service.name}`}
                          checked={marcada.has(service.id)}
                          onChange={(event) => alternarServico(service.id, event.target.checked)}
                        />
                        <span
                          className={service.isActive ? undefined : 'text-(--color-content-muted)'}
                        >
                          {service.name}
                          {!service.isActive && ' (desativado)'}
                        </span>
                      </label>
                    ))}
                  </fieldset>
                ))
              )}
            </div>
          </fieldset>

          <fieldset className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <legend className="text-sm font-medium">Itens de verificação</legend>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={itens.length >= MAX_CHECKLIST_ITEMS}
                onClick={() =>
                  setItens((atuais) => [...atuais, { label: '', method: '', tolerance: '' }])
                }
              >
                <Plus aria-hidden="true" />
                Item
              </Button>
            </div>
            <p className="text-xs text-(--color-content-muted)">
              Na ordem da inspeção. Linha sem rótulo é descartada ao gravar.
            </p>
            <ol className="flex flex-col gap-2">
              {itens.map((item, indice) => (
                <li
                  key={item.id ?? `novo-${String(indice)}`}
                  className="grid gap-2 rounded-md bg-(--color-surface-muted) p-2 md:grid-cols-[2rem_1fr_1fr_10rem_2.5rem] md:items-center"
                >
                  <span className="tabular text-xs text-(--color-content-muted)">
                    {indice + 1}.
                  </span>
                  <Input
                    aria-label={`Rótulo do item ${String(indice + 1)}`}
                    placeholder="Item de verificação"
                    value={item.label}
                    onChange={(event) => alterarItem(indice, { label: event.target.value })}
                  />
                  <Input
                    aria-label={`Método do item ${String(indice + 1)}`}
                    placeholder="Método de verificação"
                    value={item.method}
                    onChange={(event) => alterarItem(indice, { method: event.target.value })}
                  />
                  <Input
                    aria-label={`Tolerância do item ${String(indice + 1)}`}
                    placeholder="Tolerância"
                    value={item.tolerance}
                    onChange={(event) => alterarItem(indice, { tolerance: event.target.value })}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-11 md:size-9"
                    aria-label={`Remover item ${String(indice + 1)}`}
                    onClick={() =>
                      setItens((atuais) => atuais.filter((_, posicao) => posicao !== indice))
                    }
                  >
                    <Trash2 aria-hidden="true" />
                  </Button>
                </li>
              ))}
            </ol>
          </fieldset>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isPending || name.trim() === '' || !temItem || !amostragemValida}
            >
              {isPending ? 'Salvando…' : isEditing ? 'Salvar alterações' : 'Criar checklist'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
